import { RefObject, useLayoutEffect, useState } from 'react';

export type TraceBreakpoint = 'mobile' | 'tablet' | 'desktop';

export interface TraceSize {
  width: number;
  height: number;
  breakpoint: TraceBreakpoint;
}

// Container-width thresholds. Three rungs only — anything narrower than tablet
// is mobile, anything wider than tablet is desktop.
const TABLET_MIN_WIDTH = 640;
const DESKTOP_MIN_WIDTH = 895;

// Hysteresis: once we're at a breakpoint, we don't switch out of it until the
// width crosses the boundary by at least this much. Prevents flicker when a
// container width oscillates around 640 / 895 (e.g. mobile address bar
// hide/show, scrollbar appearance, plotly's own internal resizing).
const BREAKPOINT_HYSTERESIS_PX = 24;

// Sub-pixel and hairline width changes don't matter for layout decisions and
// shouldn't trigger React renders. Only update state when the width changes
// by at least this many pixels.
const SIZE_CHANGE_TOLERANCE_PX = 2;

const breakpointFor = (
  width: number,
  prev: TraceBreakpoint,
): TraceBreakpoint => {
  // Apply hysteresis based on the previous breakpoint so we don't flip-flop
  // at the exact boundaries.
  if (prev === 'mobile') {
    return width >= TABLET_MIN_WIDTH + BREAKPOINT_HYSTERESIS_PX
      ? 'tablet'
      : 'mobile';
  }
  if (prev === 'desktop') {
    return width < DESKTOP_MIN_WIDTH - BREAKPOINT_HYSTERESIS_PX
      ? 'tablet'
      : 'desktop';
  }
  // tablet
  if (width < TABLET_MIN_WIDTH - BREAKPOINT_HYSTERESIS_PX) {
    return 'mobile';
  }
  if (width >= DESKTOP_MIN_WIDTH + BREAKPOINT_HYSTERESIS_PX) {
    return 'desktop';
  }
  return 'tablet';
};

const initialBreakpointFor = (width: number): TraceBreakpoint => {
  if (width < TABLET_MIN_WIDTH) {
    return 'mobile';
  }
  if (width < DESKTOP_MIN_WIDTH) {
    return 'tablet';
  }
  return 'desktop';
};

/**
 * Observes the size of a DOM element via ResizeObserver and returns
 * width / height plus a 3-tier `breakpoint`. Used by TraceProvider so that
 * consumers can make layout decisions based on the actual container the
 * Trace is rendered into — not the browser viewport.
 *
 * Behavior tuned to stop the resize "vertigo" we saw on mobile:
 *  - rAF-coalesced updates avoid running React reconciliation on every
 *    ResizeObserver tick (browsers can fire many per frame during a scroll).
 *  - Sub-pixel width changes are ignored.
 *  - Breakpoint changes are gated by hysteresis so widths oscillating around
 *    640 / 895 do not toggle the layout repeatedly.
 */
export function useTraceSize(ref: RefObject<HTMLElement | null>): TraceSize {
  const [size, setSize] = useState<TraceSize>(() => ({
    width: 0,
    height: 0,
    breakpoint: 'desktop',
  }));

  useLayoutEffect(() => {
    const el = ref.current;
    if (el == null || typeof ResizeObserver === 'undefined') {
      return;
    }

    // Seed synchronously from the live element so the first render has real
    // dimensions instead of 0/0/'desktop'.
    const initialWidth = el.clientWidth;
    const initialHeight = el.clientHeight;
    setSize({
      width: initialWidth,
      height: initialHeight,
      breakpoint: initialBreakpointFor(initialWidth),
    });

    let pendingWidth = initialWidth;
    let pendingHeight = initialHeight;
    let rafId: number | null = null;

    const flush = () => {
      rafId = null;
      setSize((prev) => {
        const widthChanged =
          Math.abs(prev.width - pendingWidth) >= SIZE_CHANGE_TOLERANCE_PX;
        const heightChanged =
          Math.abs(prev.height - pendingHeight) >= SIZE_CHANGE_TOLERANCE_PX;
        const nextBreakpoint = breakpointFor(pendingWidth, prev.breakpoint);
        const breakpointChanged = nextBreakpoint !== prev.breakpoint;

        if (!widthChanged && !heightChanged && !breakpointChanged) {
          return prev;
        }

        return {
          width: widthChanged ? pendingWidth : prev.width,
          height: heightChanged ? pendingHeight : prev.height,
          breakpoint: nextBreakpoint,
        };
      });
    };

    const ro = new ResizeObserver((entries) => {
      const entry = entries[entries.length - 1];
      if (entry == null) {
        return;
      }
      pendingWidth = entry.contentRect.width;
      pendingHeight = entry.contentRect.height;
      if (rafId == null) {
        rafId = requestAnimationFrame(flush);
      }
    });
    ro.observe(el);
    return () => {
      ro.disconnect();
      if (rafId != null) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [ref]);

  return size;
}
